import { WEIGHED_TOLERANCE } from './delivery';

/**
 * Soát phiếu giao hàng cuối ngày (anh Tiến 14/10/2026, docs/phases/P11.md §11).
 * Mỗi đơn ERP có phát sinh trong ngày (phiếu giao, hoặc SO3 Asia nối đơn) → so 3 con số theo từng mã:
 *   SL đặt (đơn ERP) · SL trên SO3 Asia (cộng mọi SO3 của đơn) · SL thực giao (mọi phiếu giao Đã giao, trừ phần trả lại).
 * Kết luận:
 *   - Giao: chưa giao / đang giao / đã giao n lần.
 *   - So SO3: chưa có SO3 · đã giao mà chưa có SO3 (cần chuyển phiếu) · SO3 chờ giao · khớp · thực giao < SO3 · thực giao > SO3 · lệch cả hai chiều.
 *   - So đơn: chưa giao · giao thiếu (còn nợ khách) · giao đủ · giao dư.
 * Hàng cân (Kg) lệch ±3% vẫn tính khớp (DEC-006). Dòng dịch vụ (vận chuyển, cẩu) bỏ qua.
 * Chỉ đọc: không ghi Asia, không đổi phiếu.
 */

export interface ReconcileLineInput {
  sku: string;
  name: string | null;
  unit: string | null;
  /** SL đặt trên đơn ERP (0 nếu mã chỉ có trên SO3) */
  ordered: number;
  /** SL trên các SO3 của đơn */
  so3: number;
  /** SL thực giao (phiếu Đã giao, trừ trả lại) */
  delivered: number;
  weighed: boolean;
  /** Dòng dịch vụ: không so */
  skip?: boolean;
}

export const RECONCILE_SO3_VERDICTS = ['not_yet', 'need_so3', 'waiting', 'match', 'short', 'over', 'mixed'] as const;
export type ReconcileSo3Verdict = (typeof RECONCILE_SO3_VERDICTS)[number];

export const RECONCILE_SO3_LABELS: Record<ReconcileSo3Verdict, string> = {
  not_yet: 'Chưa có SO3',
  need_so3: 'Đã giao, chưa có SO3',
  waiting: 'SO3 chờ giao',
  match: 'Khớp SO3',
  short: 'Thực giao < SO3',
  over: 'Thực giao > SO3',
  mixed: 'Lệch SO3 (thiếu và dư)',
};

/** Việc NV soát phiếu cần làm theo kết luận so SO3 */
export const RECONCILE_SO3_ACTIONS: Partial<Record<ReconcileSo3Verdict, string>> = {
  need_so3: 'Chuyển SO1 → SO3 theo số thực giao',
  short: 'Bớt SO3 theo số thực giao, hoặc lập phiếu giao bù',
  over: 'Sửa tăng SO3 theo số thực giao (hoặc kiểm lại phiếu giao)',
  mixed: 'Sửa SO3 từng dòng theo số thực giao',
};

export const RECONCILE_ORDER_VERDICTS = ['none', 'partial', 'full', 'over'] as const;
export type ReconcileOrderVerdict = (typeof RECONCILE_ORDER_VERDICTS)[number];

export const RECONCILE_ORDER_LABELS: Record<ReconcileOrderVerdict, string> = {
  none: 'Chưa giao',
  partial: 'Giao thiếu',
  full: 'Giao đủ',
  over: 'Giao dư',
};

/** Bộ lọc nhanh trên màn Soát phiếu */
export const RECONCILE_FILTERS = ['all', 'todo', 'so3_diff', 'need_so3', 'undelivered', 'short', 'multi', 'complete', 'gaps'] as const;
export type ReconcileFilter = (typeof RECONCILE_FILTERS)[number];

export const RECONCILE_FILTER_LABELS: Record<ReconcileFilter, string> = {
  all: 'Tất cả',
  todo: 'Cần xử lý',
  so3_diff: 'Lệch SO3',
  need_so3: 'Đã giao, chưa có SO3',
  undelivered: 'Chưa giao',
  short: 'Giao thiếu so đơn',
  multi: 'Giao từ 2 lần',
  complete: 'Đủ bước',
  gaps: 'Đã giao · thiếu bước',
};

/**
 * Đơn đã giao có đủ bước chưa (anh Tiến 21/10/2026: "đơn giao hoàn thành có đủ các bước. Chỉ thiếu xuất hóa đơn là ok. Đánh dấu các đơn
 * chưa đủ thông tin" – "Hiển thị. Để kiểm soát phiếu trên Asia thôi"). Chỉ hiển thị, không ghi gì: Kiểm soát tự xử lý trên Asia.
 * Bước xét: Phiếu SO3 khớp thực giao, Hợp đồng, Ảnh xuất hàng, Giao đủ, Biên bản ký nhận. Hóa đơn không xét.
 */
export type ReconcileStepsVerdict = 'not_delivered' | 'complete' | 'gaps';
export const RECONCILE_STEPS_LABELS: Record<ReconcileStepsVerdict, string> = {
  not_delivered: 'Chưa giao',
  complete: 'Đủ bước',
  gaps: 'Thiếu bước',
};

const r3 = (v: number) => Math.round(v * 1000) / 1000;

/** Chênh lệch có đáng kể không: hàng cân cho ±3% của số gốc, hàng khác lệch > 0,0005 là lệch. */
function differs(base: number, actual: number, weighed: boolean): -1 | 0 | 1 {
  const tol = weighed ? Math.abs(base) * WEIGHED_TOLERANCE : 0.0005;
  const d = r3(actual - base);
  if (d > tol) return 1;
  if (d < -tol) return -1;
  return 0;
}

export interface ReconcileLineResult extends ReconcileLineInput {
  /** thực giao − SO3 */
  diffSo3: number;
  /** thực giao − đặt */
  diffOrder: number;
  so3Flag: -1 | 0 | 1;
  orderFlag: -1 | 0 | 1;
}

export interface ReconcileResult {
  so3: ReconcileSo3Verdict;
  order: ReconcileOrderVerdict;
  lines: ReconcileLineResult[];
  /** Cần NV soát phiếu xử lý (đã giao xong mà lệch SO3 / chưa có SO3) */
  needsAction: boolean;
}

/**
 * Kết luận 1 đơn.
 * `hasSo3`: đơn có ít nhất 1 SO3 còn trên Asia; `openNotes`: số phiếu giao còn mở (Chờ giao / Đang giao).
 * So SO3 chỉ khi đơn không còn phiếu mở và đã giao ít nhất 1 lần (đang giao thì chưa kết luận lệch).
 */
export function reconcileOrder(input: { lines: ReconcileLineInput[]; hasSo3: boolean; openNotes: number }): ReconcileResult {
  const lines: ReconcileLineResult[] = input.lines.map((l) => ({
    ...l,
    diffSo3: r3(l.delivered - l.so3),
    diffOrder: r3(l.delivered - l.ordered),
    so3Flag: l.skip ? 0 : differs(l.so3, l.delivered, l.weighed),
    orderFlag: l.skip ? 0 : differs(l.ordered, l.delivered, l.weighed),
  }));
  const goods = lines.filter((l) => !l.skip);
  const delivered = goods.some((l) => l.delivered > 0);

  let so3: ReconcileSo3Verdict;
  if (!input.hasSo3) so3 = delivered ? 'need_so3' : 'not_yet';
  else if (!delivered || input.openNotes > 0) so3 = 'waiting';
  else {
    const short = goods.some((l) => l.so3Flag < 0);
    const over = goods.some((l) => l.so3Flag > 0);
    so3 = short && over ? 'mixed' : short ? 'short' : over ? 'over' : 'match';
  }

  let order: ReconcileOrderVerdict;
  const ordered = goods.filter((l) => l.ordered > 0);
  if (!delivered) order = 'none';
  else if (goods.some((l) => l.orderFlag > 0)) order = 'over';
  else if (ordered.every((l) => l.orderFlag === 0)) order = 'full';
  else order = 'partial';

  const needsAction = input.openNotes === 0 && (so3 === 'need_so3' || so3 === 'short' || so3 === 'over' || so3 === 'mixed');
  return { so3, order, lines, needsAction };
}
